import type { StufenBeschreibung } from '@luzula/luzula-lib';
import { grauTreppe } from './stufen-farbtreppe';

/**
 * Festigkeitsstufen nach SEA 95, Teil B, Abschnitt 3.2.8.11 „Festigkeit"
 * (S. 76 f.).
 *
 * Gemeint ist der Widerstand, den die Bodenlage in natürlicher Lagerung dem
 * Spaten, dem Häkchen und dem Taschenmesser entgegensetzt — NICHT die
 * Zerdrückbarkeit einzelner Gefügekörper zwischen den Fingern. Der frühere
 * Hilfetext beschrieb die Zerdrückbarkeit und berief sich dabei auf SEA Teil B;
 * dort steht sie nicht.
 *
 * Zwei Felder in der App hängen daran, deshalb liegt die Reihe hier und nicht
 * in einem der Fenster:
 *  - Spalte „Festigkeit" im Grubenprotokoll (eigene Aufnahme, f0 … f4),
 *  - Spalte „Lagerungsdichte" der Substratfolge — Anlage 2 der
 *    Kartierungsanleitung nennt die Festigkeit als Bezug dafür.
 */
export interface FestigkeitStufe {
	code: string;
	name: string;
	/** Ansprache im Gelände, nach SEA. */
	ansprache: string;
	/** Beispiel aus der Anweisung; nicht jede Stufe führt eines. */
	beispiel?: string;
}

export const FESTIGKEIT_STUFEN: FestigkeitStufe[] = [
	{
		code: 'f0',
		name: 'lose',
		ansprache: 'Alle Geräte dringen mühelos in den Boden ein.',
		beispiel: 'locker gelagerte Sande',
	},
	{
		code: 'f1',
		name: 'etwas fest',
		ansprache: 'Spaten und Taschenmesser dringen erst bei mäßigem Druck ein.',
		beispiel: 'breite Staubsandbänder in pleistozänen Sanden, erdfrisch',
	},
	{
		code: 'f2',
		name: 'mäßig fest',
		ansprache: 'Eindringen mit dem Spaten nur bei starker Anstrengung möglich. Beim Hacken ist schon ein mäßiges Ausholen erforderlich.',
		beispiel: 'Bt-Horizonte vieler Geschiebelehme, erdfrisch',
	},
	{
		code: 'f3',
		name: 'sehr fest',
		ansprache: 'Eindringen mit dem Spaten nicht mehr möglich, sondern nur mit Kreuzhacke oder Erkunderhäkchen bei großer Anstrengung.',
		beispiel: 'Bt-Horizonte vieler Geschiebelehme, erdtrocken',
	},
	{
		code: 'f4',
		name: 'extrem fest oder gesteinsartig',
		ansprache: 'Eindringen auch mit der Kreuzhacke kaum noch möglich.',
	},
];

/**
 * Derselbe Inhalt als Textblock für das einfache Hilfefenster am Feld
 * „Festigkeit" im Grubenprotokoll. Eine zweite, abweichende Fassung im
 * Hilfedienst wäre eine zweite Wahrheit — und die läuft auseinander.
 */
export function festigkeitHilfetext(): string {
	const kopf = 'Festigkeit (f0–f4) — nur Grubenaufnahme\n\n'
		+ 'Widerstand der Lage in natürlicher Lagerung gegen Spaten, Häkchen und\n'
		+ 'Taschenmesser. Die Ansprache hängt vom Feuchtezustand ab: am erdfrischen\n'
		+ 'Profil ansprechen und später am ausgetrockneten Profil ergänzen.\n';
	const zeilen = FESTIGKEIT_STUFEN.map(s => {
		const beispiel = s.beispiel ? ` (z. B. ${s.beispiel})` : '';
		return `\n${s.code} — ${s.name}: ${s.ansprache}${beispiel}`;
	});
	return kopf + zeilen.join('') + '\n\nSEA 95, Teil B, Abschnitt 3.2.8.11, S. 76 f.';
}

/**
 * Für das Stufen-Feld „Festigkeit" im Grubenprotokoll: Eingabewert = Ziffer
 * des Kurzzeichens (f0 … f4 → 0 … 4). Keine eigene Legende, deshalb die graue
 * Treppe; als Name der Name der Stufe.
 */
export const FESTIGKEIT_STUFEN_FELD: StufenBeschreibung[] = (() => {
	const farben = grauTreppe(FESTIGKEIT_STUFEN.length);
	return FESTIGKEIT_STUFEN.map((s, i) => ({ wert: i, farbe: farben[i], name: s.name }));
})();
